import React, { useState, useEffect, useCallback } from "react";
import {
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  FormLabel,
  IconButton,
  Select,
  Wrap,
  WrapItem,
  Checkbox,
} from "@chakra-ui/react";
import { MdDelete, MdEdit, MdAdd } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import InsertRole from "./insertRole";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";

const ROLES_PAGE_ID = PAGE_IDS.ROLES;
const PAGES_PAGE_ID = PAGE_IDS.PAGES;

const emptyPermission = () => ({
  pageId: "",
  pageAccess: false,
  readAllAccess: false,
  readOneAccess: false,
  insertAccess: false,
  updateAccess: false,
  deleteAccess: false,
  exportAccess: false,
});

export const RoleList = () => {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [editableRole, setEditableRole] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const toast = useToast();
  const modalSize = useBreakpointValue({ base: "full", md: "xl" });
  const isMobile = useBreakpointValue({ base: true, md: false });
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const tableHeaderColor = useColorModeValue("gray.600", "gray.200");
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const createModalBg = useColorModeValue("white", "gray.800");
  const [pageOptions, setPageOptions] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });

  // Fetch page options from API
  useEffect(() => {
    const fetchPages = async () => {
      setLoadingPages(true);
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_NEWS_API_URL}/page?page_id=${PAGES_PAGE_ID}`
        );
        if (response && Array.isArray(response.data)) {
          setPageOptions(response.data);
        } else {
          toast({
            title: t("Failed to fetch pages"),
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
        }
      } catch (err) {
        toast({
          title: t("Failed to fetch pages"),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } finally {
        setLoadingPages(false);
      }
    };
    fetchPages();
  }, [t]);

  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) {
        toast({
          title: "No permissions found",
          description: "You don't have the required access.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }
      const permission = role.permissions.find((p) => p.page_id === ROLES_PAGE_ID);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      toast({
        title: "Error loading permissions",
        description: error.message || "Something went wrong while fetching permissions.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/role?page_id=${ROLES_PAGE_ID}`);
      if (!response) {
        throw new Error("Failed to fetch roles");
      }
      setRoles(response.data || []);
      setFilteredRoles(response.data || []);
    } catch (error) {
      toast({
        title: "An error occurred.",
        description: error.message || "Failed to fetch roles.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  }, [toast]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Called by InsertRole after a successful create — refreshes the list in
  // place (React re-render) instead of a full page reload.
  const handleRoleCreated = () => {
    setShowCreateRoleModal(false);
    fetchRoles();
  };

  const handleDeleteRole = async (roleId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/role`,
        "DELETE",
        { page_id: ROLES_PAGE_ID, roleId }
      );
      if (!response) {
        throw new Error("Failed to delete role");
      }
      setRoles((prevRoles) => prevRoles.filter((role) => role.id !== roleId));
      setFilteredRoles((prevFilteredRoles) => prevFilteredRoles.filter((role) => role.id !== roleId));
      toast({
        title: "Role deleted successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: "An error occurred.",
        description: error.message || "Failed to delete role.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const handleEditRole = (role) => {
    setEditableRole({
      ...role,
      permissions: (role.privileges || []).map((p) => ({ ...emptyPermission(), ...p })),
    });
  };

  const handleSaveRole = async () => {
    try {
      setIsSubmitting(true);
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/role`,
        "PUT",
        {
          page_id: ROLES_PAGE_ID,
          roleId: editableRole.id,
          roleName: editableRole.roleName,
          permissions: editableRole.permissions.map((perm) => ({
            pageId: Number(perm.pageId),
            pageAccess: perm.pageAccess,
            readAllAccess: perm.readAllAccess,
            readOneAccess: perm.readOneAccess,
            insertAccess: perm.insertAccess,
            updateAccess: perm.updateAccess,
            deleteAccess: perm.deleteAccess,
            exportAccess: perm.exportAccess,
          })),
        }
      );
      if (!response) {
        throw new Error("Failed to update role");
      }
      setRoles((prevRoles) =>
        prevRoles.map((role) => (role.id === editableRole.id ? { ...role, roleName: editableRole.roleName, privileges: editableRole.permissions } : role))
      );
      setFilteredRoles((prevFilteredRoles) =>
        prevFilteredRoles.map((role) => (role.id === editableRole.id ? { ...role, roleName: editableRole.roleName, privileges: editableRole.permissions } : role))
      );
      setEditableRole(null);
      toast({
        title: "Role updated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: "An error occurred.",
        description: error.message || "Failed to update role.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    const filtered = roles.filter((role) =>
      role.roleName.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredRoles(filtered);
  };

  const handlePermissionChange = (index, field, value) => {
    const updatedPermissions = [...editableRole.permissions];
    updatedPermissions[index][field] = value;
    setEditableRole({ ...editableRole, permissions: updatedPermissions })
  };

  const removePermission = (index) => {
    const updatedPermissions = editableRole.permissions.filter((_, i) => i !== index);
    setEditableRole({ ...editableRole, permissions: updatedPermissions })
  };

  const addPermission = () => {
    setEditableRole({ ...editableRole, permissions: [...editableRole.permissions, emptyPermission()] });
  };

  const handleSelectAllForPermission = (index, value) => {
    const updatedPermissions = editableRole.permissions.map((perm, i) =>
      i === index
        ? {
          ...perm,
          pageAccess: value,
          readAllAccess: value,
          readOneAccess: value,
          insertAccess: value,
          updateAccess: value,
          deleteAccess: value,
          exportAccess: value,
        }
        : perm
    );
    setEditableRole({ ...editableRole, permissions: updatedPermissions });
  };

  const renderPermissionsEditor = () => (
    <Box mt="6">
      <Text fontSize="lg" fontWeight="bold" mb="2">
        {t("Permissions")}
      </Text>
      <Accordion allowToggle>
        {editableRole.permissions.map((perm, index) => (
          <AccordionItem key={index} borderWidth="1px" borderRadius="lg" mb={4}>
            <h2>
              <AccordionButton _expanded={{ bg: "gray.100" }}>
                <Box flex="1" textAlign="left">
                  {t("Page")}: {pageOptions.find(p => p.id === parseInt(perm.pageId))?.pageName || t("Select a Page")}
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <Box>
                <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                  {t("Page")}
                </FormLabel>
                <Select
                  variant="filled"
                  value={perm.pageId}
                  onChange={(e) =>
                    handlePermissionChange(index, "pageId", e.target.value)
                  }
                  required
                  isDisabled={loadingPages}
                >
                  <option value="">{loadingPages ? t("Loading...") : t("Select a Page")}</option>
                  {pageOptions.map((page) => (
                    <option key={page.id} value={page.id}>
                      {t(page.pageName)}
                    </option>
                  ))}
                </Select>
              </Box>
              <Flex mt={4} gap={2}>
                <Button
                  variant="outline"
                  borderColor={GOLD_BORDER}
                  color={GOLD_TEXT}
                  size="xs"
                  _hover={{ bg: GOLD_LIGHT }}
                  onClick={() => handleSelectAllForPermission(index, true)}
                >
                  {t("Select All")}
                </Button>
                <Button
                  variant="outline"
                  borderColor="gray.200"
                  color="gray.700"
                  size="xs"
                  onClick={() => handleSelectAllForPermission(index, false)}
                >
                  {t("Deselect All")}
                </Button>
              </Flex>
              <Box mt={4}>
                <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                  {t("Access Rights")}
                </FormLabel>
                <Wrap spacing={4}>
                  {["pageAccess", "readAllAccess", "readOneAccess", "insertAccess", "updateAccess", "deleteAccess", "exportAccess"].map((field) => (
                    <WrapItem key={field}>
                      <Checkbox
                        colorScheme="brand"
                        isChecked={perm[field]}
                        onChange={(e) =>
                          handlePermissionChange(index, field, e.target.checked)
                        }
                      >
                        <Text fontSize="sm">
                          {t(field.replace(/([A-Z])/g, " $1").toUpperCase())}
                        </Text>
                      </Checkbox>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
              <Flex justify="flex-end" mt={4}>
                <IconButton
                  aria-label="Remove permission"
                  icon={<DeleteIcon />}
                  colorScheme="red"
                  variant="outline"
                  size="sm"
                  onClick={() => removePermission(index)}
                />
              </Flex>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
      <Button
        leftIcon={<AddIcon />}
        bg={GOLD_BORDER}
        color="#1A1A1A"
        _hover={{ bg: GOLD_TEXT, color: "white" }}
        _active={{ bg: GOLD_TEXT, color: "white" }}
        size="sm"
        mt={4}
        onClick={addPermission}
      >
        {t("Add Permission")}
      </Button>
    </Box>
  );

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }}>
      <Flex px={{ base: "10px", md: "25px" }} justify="space-between" mb="20px" align="center" flexWrap="wrap">
        <Flex align="center">
          <Text color="gray.800" fontSize={{ base: "18px", md: "22px" }} fontWeight="700" lineHeight="100%" mb={{ base: "10px", md: "0" }}>
            {t("Role List")}
          </Text>
          <Button
            onClick={() => setShowCreateRoleModal(true)}
            mb={1}
            isDisabled={!permissions.canInsert}
            ml={4}
            bg={GOLD_BORDER}
            color="#1A1A1A"
            _hover={{ bg: GOLD_TEXT, color: "white" }}
            _active={{ bg: GOLD_TEXT, color: "white" }}
            leftIcon={<MdAdd />}
            borderRadius="full"
            boxShadow="sm"
            transition="all 0.2s"
          >
            {t("Add Role")}
          </Button>
        </Flex>
        <Input placeholder={t("Search role")} value={searchQuery} onChange={handleSearch} size="sm" w={{ base: "100%", md: "200px" }} />
      </Flex>

      {isMobile ? (
        /** MOBILE VIEW - Render as Cards **/
        <Box overflowY={"auto"} maxH={"70vh"} px="10px">
          <SimpleGrid columns={{ base: 1 }} spacing={4} px="10px">
            {filteredRoles.map((role) => (
              <Card key={role.id} p="4" borderRadius="md" shadow="md">
                <Box mb="3">
                  <Text fontSize="lg" fontWeight="bold">
                    {role.roleName}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm">
                    <b>{t("Permissions")}: </b>
                    {(role.privileges || []).length}
                  </Text>
                  <Flex mt="2">
                    <Button
                      variant="outline"
                      borderColor={GOLD_BORDER}
                      color={GOLD_TEXT}
                      _hover={{ bg: GOLD_LIGHT }}
                      size="xs"
                      mr="2"
                      isDisabled={!permissions.canEdit}
                      onClick={() => handleEditRole(role)}
                    >
                      <MdEdit />
                    </Button>
                    <Button
                      colorScheme="red"
                      variant="outline"
                      size="xs"
                      isDisabled={!permissions.canDelete}
                      mr="2"
                      onClick={() => handleDeleteRole(role.id)}
                    >
                      <MdDelete />
                    </Button>
                  </Flex>
                </Box>
              </Card>
            ))}
          </SimpleGrid>
        </Box>
      ) : (
        /** DESKTOP VIEW - Render as Table **/
        <Box overflowX="auto" overflowY={"auto"} maxH={"60vh"} px="0px">
          <Table variant="simple">
            <Thead>
              <Tr bg={tableHeaderBg}>
                <Th color={tableHeaderColor} fontWeight="600">{t("Role Name")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Permissions")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Actions")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredRoles.map((role) => (
                <Tr key={role.id} borderBottom="1px solid" borderColor="gray.100" _hover={{ bg: rowHoverBg }}>
                  <Td>{role.roleName}</Td>
                  <Td>{(role.privileges || []).length}</Td>
                  <Td>
                    <Flex>
                      <Button
                        variant="outline"
                        borderColor={GOLD_BORDER}
                        color={GOLD_TEXT}
                        _hover={{ bg: GOLD_LIGHT }}
                        size="sm"
                        mr="2"
                        isDisabled={!permissions.canEdit}
                        onClick={() => handleEditRole(role)}
                      >
                        <MdEdit />
                      </Button>
                      <Button
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        mr="2"
                        isDisabled={!permissions.canDelete}
                        onClick={() => handleDeleteRole(role.id)}
                      >
                        <MdDelete />
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {editableRole && (
        <Modal isOpen={true} onClose={() => setEditableRole(null)} size={modalSize}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader borderBottom="1px solid" borderColor="gray.100">
              <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
                {t("Edit Role")}
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box>
                <Text fontSize="sm" color="gray.600" mb="2">
                  {t("Role Name")}
                </Text>
                <Input
                  bg="white"
                  borderColor={GOLD_BORDER}
                  focusBorderColor={GOLD_BORDER}
                  value={editableRole.roleName}
                  onChange={(e) => setEditableRole({ ...editableRole, roleName: e.target.value })}
                />
              </Box>
              {renderPermissionsEditor()}
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
              <Button
                variant="outline"
                borderColor="gray.200"
                color="gray.700"
                borderRadius="full"
                onClick={() => setEditableRole(null)}
              >
                {t("Cancel")}
              </Button>
              <Button
                bg={GOLD_BORDER}
                color="#1A1A1A"
                _hover={{ bg: GOLD_TEXT, color: "white" }}
                _active={{ bg: GOLD_TEXT, color: "white" }}
                onClick={handleSaveRole}
                isLoading={isSubmitting}
                borderRadius="full"
                boxShadow="md"
              >
                {t("Save")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      <Modal isOpen={showCreateRoleModal} onClose={() => setShowCreateRoleModal(false)} size={modalSize}>
        <ModalOverlay />
        <ModalContent borderRadius="lg" boxShadow="xl" bg={createModalBg}>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100">
            <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
              {t("Create Role")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <InsertRole onCreated={handleRoleCreated} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default RoleList;
